import Component from '@glimmer/component';
import Button from 'tio-ui/components/button';
import CheckboxList from 'tio-ui/components/checkbox-list';
import FilterRail from 'tio-ui/components/filter-rail';
import Text from 'tio-ui/components/text';
import type { CheckboxListItem } from 'tio-ui/components/checkbox-list';

/** One checkbox facet group in the rail. Values within a group OR; groups AND. */
export interface PeopleFacetGroup {
  /** Stable identity for the `{{#each}}`, not rendered. */
  key: string;
  /** Group heading; translated by the caller. */
  label: string;
  /** Already-labelled and counted; an empty list hides the whole group. */
  items: CheckboxListItem[];
  /** Subset of `items` keys currently applied. */
  selectedKeys: string[];
  /** Receives the full new selection, not a delta. */
  onChange: (keys: string[]) => void;
}

interface S {
  Args: {
    /** Accessible name for the rail region. */
    label: string;
    /** Rendered in order; a group with no items is skipped entirely. */
    groups: PeopleFacetGroup[];
    /** Copy shown in place of the groups before any aggregation has run. */
    hint?: string;
    /** Gates the clear-all control. Owned by the caller, since only it knows
     * whether non-facet state (a search term, say) also counts as a filter. */
    hasActiveFilters: boolean;
    /** Label for the clear-all control; translated by the caller. */
    clearLabel: string;
    /** Clears every filter the caller considers active, facets included. */
    onClearAll: () => void;
  };
  Blocks: {
    /** Controls rendered above the facet groups — e.g. an entry-point typeahead. */
    leading: [];
  };
  Element: HTMLElement;
}

/**
 * Faceted filter rail for the people search surfaces. Renders whatever groups
 * the caller supplies, so each app owns its own facet vocabulary and scoping
 * while the rail owns the chrome: group order, the empty-state hint, and the
 * clear-all affordance.
 *
 * Counts belong in the item labels (see `withCount`) rather than a separate
 * arg — `CheckboxList` renders one string per option.
 */
export default class PeopleFilterRail extends Component<S> {
  get visibleGroups(): PeopleFacetGroup[] {
    return this.args.groups.filter((group) => group.items.length > 0);
  }

  <template>
    <FilterRail @label={{@label}} ...attributes>
      {{yield to="leading"}}

      {{#if this.visibleGroups.length}}
        {{#each this.visibleGroups key="key" as |group|}}
          <CheckboxList
            @label={{group.label}}
            @items={{group.items}}
            @selectedKeys={{group.selectedKeys}}
            @onChange={{group.onChange}}
          />
        {{/each}}
      {{else if @hint}}
        <Text @variant="muted" @size="sm">{{@hint}}</Text>
      {{/if}}

      {{#if @hasActiveFilters}}
        <Button @appearance="minimal" @intent="danger" {{on "click" @onClearAll}}>
          {{@clearLabel}}
        </Button>
      {{/if}}
    </FilterRail>
  </template>
}
